'use client';
import React, { FC, useState, Fragment, MouseEvent } from 'react';
import { Entry } from 'contentful';
import classNames from 'classnames';
import { TypeExplorerSliceFields } from '@/core/generated-types';

interface ExplorerSwitchProps {
  slices: Entry<TypeExplorerSliceFields>[] | undefined;
  theme: 'primary' | 'red' | 'default';
}

const anchorColorStyle = {
  primary: 'text-blue-deep hover:text-[#131C66] active:text-[#131C66]',
  red: 'text-red-v2 hover:text-red-v2-hover active:text-red-v2-hover',
  default: 'text-blue-deep hover:text-[#131C66] active:text-[#131C66]',
};

const ExplorerSwitch: FC<ExplorerSwitchProps> = ({ slices, theme }) => {
  const sliceSwitchItems = slices?.map(
    (slice: Entry<TypeExplorerSliceFields>) => ({
      anchorId: slice?.fields?.anchorId,
      anchorText: slice?.fields?.anchorText,
    }),
  );

  const [activeAnchorId, setActiveAnchorId] = useState<string | undefined>(
    sliceSwitchItems?.[0]?.anchorId,
  );

  const numberOfItems = sliceSwitchItems?.length || 0;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    e.preventDefault();
    const targetElement = document.getElementById(anchorId);

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <div className="mx-auto my-6 flex flex-wrap justify-center text-d-base md:gap-4 md:text-d-h3 lg:font-bold">
      {sliceSwitchItems &&
        sliceSwitchItems.map(({ anchorText, anchorId }, index) => {
          const isActiveAnchor = activeAnchorId === anchorId;

          // Insert a break to force a new row and avoid displaying an element alone on the last row on mobile.
          const isAfterItemThreeOutOfFive = index === 3 && numberOfItems === 5;
          const rowBreakForMobileViewport = isAfterItemThreeOutOfFive && (
            <div className="w-[100%] basis-auto md:hidden" />
          );
          return (
            <Fragment key={anchorId}>
              {rowBreakForMobileViewport}
              <div className="flex justify-center">
                <div
                  className={classNames(
                    isActiveAnchor && theme === 'default'
                      ? ' bg-anchorLinkWatercolorBg bg-[length:88px] bg-[center_top_2px] bg-no-repeat'
                      : '',
                  )}
                  onClick={() => setActiveAnchorId(anchorId)}
                >
                  <a
                    href={`#${anchorId}`}
                    onClick={(e) => handleClick(e, anchorId)}
                    className={`flex h-8 md:h-[80px] items-center px-2 lg:px-4 underline ${anchorColorStyle[theme]}`}
                  >
                    {anchorText}
                  </a>
                </div>
              </div>
            </Fragment>
          );
        })}
    </div>
  );
};

export default React.memo(ExplorerSwitch);
